import cn from 'classnames';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { ReactComponent as ChevronIcon } from '@assets/svg/chevron.svg';
import { ReactComponent as TrapezeIcon } from '@assets/svg/trapeze.svg';
import styles from './slider.module.scss';
import { v4 as uuid } from 'uuid';

interface IProps {
  items: ReactNode[];
  className?: string;
}

/** Time expressed in Milliseconds */
const DISPLAY_TIME_PER_SLIDE = 10000;

const Slider = ({ items, className = '' }: IProps): JSX.Element => {
  const [activeSlide, setActiveSlide] = useState<number>(0);

  /* Automatic slide change */

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const slideChangeInterval: NodeJS.Timer = setInterval(
      nextSlide,
      DISPLAY_TIME_PER_SLIDE
    );

    return () => clearInterval(slideChangeInterval);
  }, [activeSlide, items]);

  /* Handlers */

  const nextSlide = (): void => {
    const nextSlideIndex: number = activeSlide + 1;

    setActiveSlide(nextSlideIndex > items.length - 1 ? 0 : nextSlideIndex);
  };

  const prevSlide = (): void => {
    const prevSlideIndex: number = activeSlide - 1;

    setActiveSlide(prevSlideIndex < 0 ? items.length - 1 : prevSlideIndex);
  };

  const goToSlide = (index: number): void => setActiveSlide(index);

  /* ClassNames */

  const getActiveClassNames =
    (className: string) =>
    (index: number): string =>
      cn(styles[className], {
        [styles.active]: index === activeSlide,
      });

  const lineClassNames = getActiveClassNames('indicators__line');
  const slideClassNames = getActiveClassNames('wrapper__item');

  /* Components */

  const SliderItems: JSX.Element[] = items.map((view, idx) => (
    <Fragment key={uuid()}>
      {activeSlide === idx && (
        <div className={slideClassNames(idx)}>{view}</div>
      )}
    </Fragment>
  ));

  const SliderLines: JSX.Element[] = items.map((_, idx) => (
    <div
      key={uuid()}
      onClick={() => goToSlide(idx)}
      className={lineClassNames(idx)}
    />
  ));

  return (
    <section className={cn(styles.slider, className)}>
      <div className={styles.wrapper}>{SliderItems}</div>

      <button className={styles.slider__prev} onClick={prevSlide}>
        <ChevronIcon className={styles.slider__icon} />
      </button>
      <button className={styles.slider__next} onClick={nextSlide}>
        <ChevronIcon className={styles.slider__icon} />
      </button>

      <div className={styles.indicators}>
        <TrapezeIcon className={styles.indicators__icon} />
        <div className={styles.indicators__wrapper}>{SliderLines}</div>
      </div>
    </section>
  );
};

export default Slider;
